import type { Channel } from "@expert/shared-types";

import { Checkbox, Radio, Stack, Text } from "@mantine/core";
import Markdown from "markdown-to-jsx";

import { useFeedbackDraftStore } from "../../store";
import { getFeedbackUIMappingNoText } from "../../types";

export function NoTextFeedback({
    isLoading,
    channel,
    isTraining,
    appName,
}: {
    isLoading: boolean;
    channel?: Channel;
    isTraining?: boolean;
    appName?: string;
}) {
    const selectedCategory = useFeedbackDraftStore((state) => state.draft.category);
    const selectedSubcategories = useFeedbackDraftStore((state) => state.draft.subcategories);
    const { subtitle, categories, subcategories } = getFeedbackUIMappingNoText(channel, isTraining, appName);
    const onChangeCategory = (value: string) => {
        const { draft, setDraft } = useFeedbackDraftStore.getState();
        setDraft({
            ...draft,
            category: value,
            subcategories: undefined,
        });
    };

    const onChangeSubcategories = (value: string[]) => {
        const description = value.join(", ");
        const { draft, setDraft } = useFeedbackDraftStore.getState();
        setDraft({
            ...draft,
            description,
            subcategories: value,
        });
    };

    return (
        <Stack>
            {subtitle && (
                <Text size="sm" c="dark.3" mb="lg">
                    <Markdown options={{ forceInline: true }}>{subtitle}</Markdown>
                </Text>
            )}
            <Radio.Group value={selectedCategory} onChange={onChangeCategory}>
                <Stack gap="lg">
                    {categories.map((category) => (
                        <Stack gap="0.5rem" key={category}>
                            <Radio
                                size="sm"
                                value={category}
                                label={category}
                                data-testid={`radio-feedback-${category}`}
                                disabled={isLoading}
                                fw={category === selectedCategory ? 700 : 400}
                            />
                            {category === selectedCategory && subcategories[category] ? (
                                <Checkbox.Group value={selectedSubcategories} onChange={onChangeSubcategories}>
                                    {subcategories[category].map((option) => (
                                        <Checkbox
                                            value={option}
                                            label={option}
                                            key={option}
                                            p="xxs"
                                            pl="xxl"
                                            data-testid={`checkbox-feedback-${option}`}
                                            disabled={isLoading}
                                        />
                                    ))}
                                </Checkbox.Group>
                            ) : null}
                        </Stack>
                    ))}
                </Stack>
            </Radio.Group>
        </Stack>
    );
}
